/**
 * Persists signup / payer OTP step: phone + entry time for refresh recovery and 3-minute expiry.
 * Stored in sessionStorage (tab-scoped, cleared when the tab ends).
 */

export const SIGNUP_OTP_SESSION_KEY = 'saee:signup-otp-session';

/** Three minutes from first arrival on the OTP step (after OTP is requested). */
export const SIGNUP_OTP_TTL_MS = 3 * 60 * 1000;

export type SignupOtpAuthContext = 'broker' | 'client';

export type SignupOtpSessionPayload = {
  phone: string;
  startedAt: number;
  authContext: SignupOtpAuthContext;
  clientDealId?: string;
  clientToken?: string;
};

export function clearSignupOtpSession(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.removeItem(SIGNUP_OTP_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function saveSignupOtpSession(params: {
  phone: string;
  authContext: SignupOtpAuthContext;
  clientDealId?: string;
  clientToken?: string;
}): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  const payload: SignupOtpSessionPayload = {
    phone: params.phone.trim(),
    startedAt: Date.now(),
    authContext: params.authContext,
    clientDealId: params.clientDealId,
    clientToken: params.clientToken,
  };
  try {
    window.sessionStorage.setItem(SIGNUP_OTP_SESSION_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota */
  }
}

export function loadSignupOtpSession(): SignupOtpSessionPayload | null {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(SIGNUP_OTP_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SignupOtpSessionPayload>;
    if (
      typeof parsed.phone !== 'string' ||
      typeof parsed.startedAt !== 'number' ||
      (parsed.authContext !== 'broker' && parsed.authContext !== 'client')
    ) {
      return null;
    }
    return {
      phone: parsed.phone,
      startedAt: parsed.startedAt,
      authContext: parsed.authContext,
      clientDealId: typeof parsed.clientDealId === 'string' ? parsed.clientDealId : undefined,
      clientToken: typeof parsed.clientToken === 'string' ? parsed.clientToken : undefined,
    };
  } catch {
    return null;
  }
}

export function isSignupOtpSessionExpired(session: SignupOtpSessionPayload): boolean {
  return Date.now() - session.startedAt > SIGNUP_OTP_TTL_MS;
}

export function signupOtpSessionMatchesRoute(
  session: SignupOtpSessionPayload,
  authContext: SignupOtpAuthContext,
  clientDealId?: string,
  clientToken?: string,
): boolean {
  if (session.authContext !== authContext) return false;
  if (authContext === 'client') {
    return session.clientDealId === clientDealId && session.clientToken === clientToken;
  }
  return true;
}

/**
 * Partially masked Saudi mobile for OTP screen (+966 5** *** XXXX).
 */
export function formatPhoneForOtpDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const national = digits.startsWith('966') ? digits.slice(3) : digits;
  if (national.length >= 9 && national.startsWith('5')) {
    const last4 = national.slice(-4);
    return `+966 ${national[0]}** *** ${last4}`;
  }
  const trimmed = phone.trim();
  if (trimmed.startsWith('+')) return trimmed;
  return `+${digits}`;
}
